import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FiscalYearsRes, FiscalYear, SelectOption } from 'models';
import { BalanceType } from 'utils/enums';

import { useCooperativeId } from 'hooks';
import { getBalanceEndDate } from 'utils';
import { buildURL } from './helpers';

import services from 'services';

import {
  extractCurrentFiscalYear,
  toDate,
  filterBalanceFiscalYears,
} from 'utils';

import { CooperativeBudgetDetails } from 'components';

interface CooperativeBalanceState {
  version: {
    label: string;
    value: number;
  };
  fiscalYearsData: FiscalYearsRes;
  selectedFiscalYears: FiscalYear | null;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
  balanceType: SelectOption<BalanceType>;
}

type Version = {
  label: string;
  value: number;
};

const CooperativeBalance = () => {
  const { t } = useTranslation();
  const coopId = useCooperativeId();

  const [state, setState] = useState<CooperativeBalanceState>({
    version: {
      label: t('#portal.report.bar.option.label.VersionSelect.Short'),
      value: 0,
    },
    fiscalYearsData: {
      ClosedPeriodEnd: null,
      FiscalYears: [],
      NotMappedPaymentDate: null,
    },
    startDate: null,
    endDate: null,
    selectedFiscalYears: null,
    loading: false,
    error: false,
    balanceType: { label: 'Kaikki', value: BalanceType.All },
  });

  useEffect(() => {
    const fetchData = async (coopId: string) => {
      setState((prevState) => ({ ...prevState, loading: true, error: false }));

      try {
        const fiscalYearsData = await fetchFiscalYears(coopId);
        const fiscalYears = filterBalanceFiscalYears(
          fiscalYearsData,
          new Date()
        );
        const currentFiscalYear = extractCurrentFiscalYear(fiscalYears);
        const startDate = toDate(currentFiscalYear?.StartDate);
        const endDate = toDate(currentFiscalYear?.EndDate);

        setState((prevState) => ({
          ...prevState,
          fiscalYearsData: {
            ClosedPeriodEnd: fiscalYearsData?.ClosedPeriodEnd || null,
            FiscalYears: fiscalYears,
            NotMappedPaymentDate: fiscalYearsData?.NotMappedPaymentDate || null,
          },
          selectedFiscalYears: currentFiscalYear,
          startDate,
          endDate: endDate ? getBalanceEndDate(endDate) : null,
          loading: false,
          error: false,
        }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: true,
        }));
      }
    };

    if (coopId) {
      fetchData(coopId);
    }
  }, [coopId]);

  const fetchFiscalYears = async (coopId: string) => {
    try {
      const res: FiscalYearsRes = (
        await services.get(`/api/cooperatives/${coopId}/fiscalyears`)
      ).data.Data;

      return res;
    } catch (err) {
      console.error(err);
    }
  };

  const handleChangeFiscalYear = useCallback((fiscalYear: FiscalYear) => {
    setState((prevState) => {
      const endDate = toDate(fiscalYear.EndDate);
      return {
        ...prevState,
        selectedFiscalYears: fiscalYear,
        startDate: toDate(fiscalYear.StartDate),
        endDate: endDate ? getBalanceEndDate(endDate) : null,
      };
    });
  }, []);

  const handleChangeStartDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));
  }, []);

  const handleChangeEndDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      endDate: date,
    }));
  }, []);

  const handleChangeVersion = useCallback((version: Version) => {
    setState((prevState) => ({
      ...prevState,
      version,
    }));
  }, []);

  const handleChangeBalanceType = useCallback(
    (option: SelectOption<BalanceType>) => {
      setState((prevState) => ({
        ...prevState,
        balanceType: option,
      }));
    },
    []
  );

  const iframeURL = useMemo(() => {
    if (
      coopId &&
      state.selectedFiscalYears &&
      state.startDate &&
      state.endDate &&
      state.version
    ) {
      return buildURL(
        coopId,
        state.selectedFiscalYears.Id,
        state.startDate,
        state.endDate,
        state.version,
        state.balanceType.value
      );
    }

    return null;
  }, [
    state.selectedFiscalYears,
    state.startDate,
    state.endDate,
    state.version,
    coopId,
    state.balanceType.value,
  ]);

  const minDate = useMemo(() => {
    return toDate(state.selectedFiscalYears?.StartDate);
  }, [state.selectedFiscalYears?.StartDate]);

  const maxDate = useMemo(() => {
    const endDate = toDate(state.selectedFiscalYears?.EndDate);

    return endDate ? getBalanceEndDate(endDate) : null;
  }, [state.selectedFiscalYears]);

  return (
    <CooperativeBudgetDetails
      {...state}
      maxDate={maxDate}
      minDate={minDate}
      fileName="Maksutilanneraportti"
      reportId="cooperative-balance"
      iframeURL={iframeURL}
      onChangeBalanceType={handleChangeBalanceType}
      onChangeStartDate={handleChangeStartDate}
      onChangeEndDate={handleChangeEndDate}
      onChangeFiscalYear={handleChangeFiscalYear}
      onChangeVersion={handleChangeVersion}
    />
  );
};

export default CooperativeBalance;
